import React, { useState, useEffect } from "react";
import { MdClose } from "react-icons/md";
import { FaRegCheckSquare, FaRegSquare } from "react-icons/fa";
import { CircularProgress } from "@mui/material";
import tw from "tailwind-styled-components";
import { Label, ScrollableContent } from "../Styles/InputStyles";
import { GoSearch } from "react-icons/go";
import { getRole, getUserType } from "../../helpers";

export default function PaidUserModal({
    isOpen,
    onClose,
    onSelect,
    options = [],
    selectedPaidBy,
    isLoading = false,
}) {
    const [searchText, setSearchText] = useState("");
    const [groupedUsers, setGroupedUsers] = useState([]);

    useEffect(() => {
        if (isOpen) {
            setSearchText("");
        }
    }, [isOpen]);

    useEffect(() => {
        const groups = {};

        options.forEach(option => {
            const userType = getUserType(option.userType || 0);
            if (!groups[userType]) {
                groups[userType] = [];
            }
            groups[userType].push({
                ...option,
                label: option.name,
                value: option.id,
                userTypeLabel: userType
            });
        });

        const groupedArray = Object.keys(groups).map(groupName => ({
            name: groupName,
            items: groups[groupName]
        }));

        setGroupedUsers(groupedArray);
    }, [options]);

    if (!isOpen) return null;

    const filteredUsers = groupedUsers.map(group => ({
        ...group,
        items: group.items.filter(user =>
            user.name?.toLowerCase().includes(searchText.toLowerCase()) ||
            user.mobile?.toLowerCase().includes(searchText.toLowerCase()) ||
            user.userTypeLabel?.toLowerCase().includes(searchText.toLowerCase())
        )
    })).filter(group => group.items.length > 0);

    const handleSelect = (user) => {
        onSelect(user);
        onClose();
    };

    const isSelected = (userId) => {
        return selectedPaidBy?.id === userId;
    };

    const getTitle = (user) => {
        if (user.userType === 1)
            return 'Client';
        else
            return getRole(user?.role || 1);
    };

    return (
        <Overlay>
            <ModalWrapper>
                <Header>
                    <h1 className="text-white text-lg font-medium">Select Paid By</h1>
                    <button onClick={onClose}>
                        <MdClose color="white" size={24} />
                    </button>
                </Header>

                <div className="p-4 relative">
                    <div className="relative">
                        <input
                            type="text"
                            placeholder="Search users..."
                            value={searchText}
                            onChange={(e) => setSearchText(e.target.value)}
                            className="w-full px-8 h-12 py-2 border border-gray-200 rounded-md focus:outline-none focus:border-primary-color text-gray-800 placeholder-gray-300"
                        />

                        <div className="absolute left-3 top-1/2 transform -translate-y-1/2">
                            <GoSearch className="w-4 h-4 text-gray-400" />
                        </div>
                    </div>
                </div>

                <ScrollableContent className="max-h-[calc(100dvh-140px)]">
                    {isLoading ? (
                        <div className="flex justify-center py-8">
                            <CircularProgress />
                        </div>
                    ) : filteredUsers.length === 0 ? (
                        <div className="text-center py-8 text-gray-500">
                            No users found
                        </div>
                    ) : (
                        filteredUsers.map((group) => (
                            <div key={group.name}>

                                <div className="px-4 py-1 bg-gray-100 border-gray-200">
                                    <Label className="text-gray-600 font-medium text-sm">
                                        {group.name}
                                    </Label>
                                </div>

                                {group.items.map((user) => (
                                    <OptionRow
                                        key={user.id}
                                        onClick={() => handleSelect(user)}
                                    >
                                        <div className="flex flex-col">
                                            <Label className="font-medium text-sm">{user.name}</Label>
                                            <div className="flex gap-2 text-xs text-gray-500 mt-1">
                                                <span>#{user.mobile}</span>
                                                <span>•</span>
                                                <span>{getTitle(user)}</span>
                                            </div>
                                        </div>
                                        <button disabled={isLoading}>
                                            {isLoading ? (
                                                <CircularProgress size={20} />
                                            ) : isSelected(user.id) ? (
                                                <FaRegCheckSquare size={20} className="text-green-500 font-normal text-sm" />
                                            ) : (
                                                <FaRegSquare size={20} className="text-gray-300 font-normal text-sm" />
                                            )}
                                        </button>
                                    </OptionRow>
                                ))}
                            </div>
                        ))
                    )}
                </ScrollableContent>
            </ModalWrapper>
        </Overlay>
    );
}

const Overlay = tw.div`fixed inset-0 z-50 flex items-center justify-end bg-black/50`
const ModalWrapper = tw.div`bg-white w-11/12 h-full flex flex-col relative`
const Header = tw.div`flex items-center justify-between bg-primary-color py-4 px-4`
const OptionRow = tw.div`ml-2 flex items-center justify-between p-4 border-b border-gray-100 cursor-pointer hover:bg-gray-50`